import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Card from '../../components/Card.jsx';
import Button from '../../components/Button.jsx';
import { useAuth } from '../../context/AuthContext.jsx';
import apiService from '../../context/apiService.js';
import { toast } from 'react-toastify';
import { 
  BookOpen, 
  Clock, 
  Star,
  CheckCircle,
  TrendingUp,
  Loader2
} from 'lucide-react';

const StudentDashboard = () => {
  const { userData } = useAuth();
  const [loading, setLoading] = useState(true);
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [recommendedCourses, setRecommendedCourses] = useState([]);
  const [stats, setStats] = useState({
    enrolledCount: 0,
    completedCount: 0,
    inProgressCount: 0,
    avgRating: 0
  });


  // Fetch recommended courses (all public courses not enrolled in)
  const fetchRecommendedCourses = async (enrolledCourseIds = []) => {
    try {
      const response = await apiService.courses.getAllPublic();
      if (response.data.success) {
        // Filter out enrolled courses
        const recommended = response.data.courses
          .filter(course => !enrolledCourseIds.includes(course._id))
          .slice(0, 6); // Show only first 6 recommended courses
        
        setRecommendedCourses(recommended);
      }
    } catch (error) {
      console.error('Error fetching recommended courses:', error);
      toast.error('Failed to load recommended courses');
    }
  };

  // Fetch all dashboard data
  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // First fetch enrolled courses
      const response = await apiService.enrollment.getEnrolledCourses();
      if (response.data.success) {
        const enrollments = response.data.enrollments || response.data.courses; // Handle both old and new API response
        setEnrolledCourses(enrollments);
        
        // Calculate stats
        const enrolledCount = enrollments.length;
        const completedCount = enrollments.filter(enrollment => {
          const progress = enrollment.progress || enrollment.course?.progress || 0;
          return progress === 100;
        }).length;
        const inProgressCount = enrolledCount - completedCount;
        const avgRating = enrollments.length > 0 ? 
          (enrollments.reduce((sum, enrollment) => {
            const course = enrollment.course || enrollment;
            return sum + (course.rating || 0);
          }, 0) / enrollments.length).toFixed(1) : 0;

        setStats({
          enrolledCount,
          completedCount,
          inProgressCount,
          avgRating
        });

        // Now fetch recommended courses with enrolled course IDs
        const enrolledCourseIds = enrollments.map(enrollment => {
          const course = enrollment.course || enrollment;
          return course._id;
        });
        await fetchRecommendedCourses(enrolledCourseIds);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userData && userData.role === 'student') {
      fetchDashboardData();
    }
  }, [userData]);

  // Calculate progress for a course
  const calculateCourseProgress = (enrollment) => {
    // If enrollment has progress property, use it
    if (enrollment.progress !== undefined) {
      return enrollment.progress;
    }
    
    // If it's the old format (direct course object)
    if (enrollment.course && enrollment.course.progress !== undefined) {
      return enrollment.course.progress;
    }
    
    // Otherwise calculate from lessons (if available)
    const course = enrollment.course || enrollment;
    if (course.lessons && course.lessons.length > 0) {
      const completedLessons = course.lessons.filter(lesson => lesson.completed).length;
      return Math.round((completedLessons / course.lessons.length) * 100);
    }
    
    return 0;
  };

  // Format enrollment date
  const formatEnrollmentDate = (enrollment) => {
    const enrolledAt = enrollment.enrolledAt || enrollment.createdAt;
    if (!enrolledAt) return 'Recently';
    
    const date = new Date(enrolledAt);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.ceil(diffDays / 30)} months ago`;
    return `${Math.ceil(diffDays / 365)} years ago`;
  };

  // Stats configuration
  const statsConfig = [
    { 
      title: 'Enrolled Courses', 
      value: stats.enrolledCount.toString(), 
      icon: BookOpen, 
      color: 'text-blue-600' 
    },
    { 
      title: 'Completed', 
      value: stats.completedCount.toString(), 
      icon: CheckCircle, 
      color: 'text-green-600' 
    },
    { 
      title: 'In Progress', 
      value: stats.inProgressCount.toString(), 
      icon: Clock, 
      color: 'text-yellow-600' 
    },
    { 
      title: 'Avg Rating', 
      value: stats.avgRating.toString(), 
      icon: Star, 
      color: 'text-purple-600' 
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center space-x-2">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>Loading dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900">My Learning</h1>
          <p className="text-secondary-600 mt-1">Continue your learning journey and discover new courses.</p>
        </div>
        <div className="flex items-center space-x-3">
          <Button 
            variant="outline" 
            onClick={fetchDashboardData}
            disabled={loading}
            className="flex items-center space-x-2"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : null}
            <span>Refresh</span>
          </Button>
        <Link to="/student/courses">
          <Button className="flex items-center space-x-2">
            <BookOpen size={20} />
            <span>Browse Courses</span>
          </Button>
        </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statsConfig.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} padding="sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-secondary-600">{stat.title}</p>
                  <p className="text-2xl font-bold text-secondary-900 mt-1">{stat.value}</p>
                </div>
                <div className={`p-3 rounded-lg bg-gray-50 ${stat.color}`}>
                  <Icon size={24} />
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Enrolled Courses */}
      <Card>
        <Card.Header>
          <div className="flex items-center justify-between">
            <Card.Title>My Courses</Card.Title>
            <Link to="/student/enrollments" className="text-sm text-primary-600 hover:text-primary-700">
              View all
            </Link>
          </div>
        </Card.Header>
        <Card.Content>
          {enrolledCourses.length === 0 ? (
            <div className="text-center py-8">
              <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No enrolled courses yet</h3>
              <p className="text-gray-600 mb-4">Start your learning journey by enrolling in a course.</p>
              <Link to="/student/courses">
                <Button>Browse Courses</Button>
              </Link>
            </div>
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {enrolledCourses.map((enrollment) => {
                const course = enrollment.course || enrollment;
                const progress = calculateCourseProgress(enrollment);
                const enrollmentDate = formatEnrollmentDate(enrollment);
                
                return (
                  <div key={enrollment._id || course._id} className="border border-secondary-200 rounded-lg overflow-hidden hover:shadow-medium transition-shadow">
                <div className="aspect-video bg-gray-200 relative course-thumbnail">
                  <img 
                        src={course.thumbnail || 'https://via.placeholder.com/300x200?text=Course'} 
                    alt={course.title}
                    className="w-full h-full object-cover object-center"
                  />
                  <div className="absolute top-2 right-2">
                    <span className="bg-white bg-opacity-90 px-2 py-1 rounded-full text-xs font-medium">
                          {progress}% Complete
                        </span>
                      </div>
                      <div className="absolute bottom-2 left-2">
                        <span className="bg-black bg-opacity-70 text-white px-2 py-1 rounded-full text-xs font-medium">
                          Enrolled {enrollmentDate}
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-secondary-900 mb-1">{course.title}</h3>
                      <p className="text-sm text-secondary-600 mb-2">by {course.creater?.name || 'Unknown Instructor'}</p>
                  
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-1">
                      <Star size={16} className="text-yellow-500 fill-current" />
                          <span className="text-sm font-medium">{course.rating || '4.5'}</span>
                    </div>
                    <span className="text-sm text-secondary-600">
                          {course.lessons?.length || 0} lessons
                    </span>
                  </div>
                  
                  <div className="mb-3">
                    <div className="flex items-center justify-between text-sm text-secondary-600 mb-1">
                      <span>Progress</span>
                          <span>{progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                  </div>
                  
                      <Link to={`/student/courses/${course._id}`}>
                    <Button className="w-full" variant="outline">
                          {progress === 100 ? 'Review Course' : 'Continue Learning'}
                    </Button>
                  </Link>
                </div>
              </div>
                );
              })}
          </div>
          )}
        </Card.Content>
      </Card>

      {/* Recommended Courses */}
      <Card>
        <Card.Header>
          <div className="flex items-center justify-between">
            <Card.Title>Recommended for You</Card.Title>
            <Link to="/student/courses" className="text-sm text-primary-600 hover:text-primary-700">
              Browse all
            </Link>
          </div>
        </Card.Header>
        <Card.Content>
          {recommendedCourses.length === 0 ? (
            <div className="text-center py-8">
              <Star className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No recommendations available</h3>
              <p className="text-gray-600 mb-4">Check back later for new course recommendations.</p>
            </div>
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {recommendedCourses.map((course) => (
                <div key={course._id} className="border border-secondary-200 rounded-lg overflow-hidden hover:shadow-medium transition-shadow">
                <div className="aspect-video bg-gray-200 course-thumbnail">
                  <img 
                      src={course.thumbnail || 'https://via.placeholder.com/300x200?text=Course'} 
                    alt={course.title}
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-secondary-900 mb-1">{course.title}</h3>
                    <p className="text-sm text-secondary-600 mb-2">by {course.creater?.name || 'Unknown Instructor'}</p>
                  
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-1">
                      <Star size={16} className="text-yellow-500 fill-current" />
                        <span className="text-sm font-medium">{course.rating || '4.5'}</span>
                    </div>
                    <span className="text-sm text-secondary-600">
                        {course.enrollments?.length || 0} students
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                      <span className="text-lg font-bold text-primary-600">${course.price || '99'}</span>
                      <Link to={`/student/courses/${course._id}`}>
                    <Button size="sm">
                          View Course
                    </Button>
                      </Link>
                    </div>
                </div>
              </div>
            ))}
          </div>
          )}
        </Card.Content>
      </Card>

      {/* Learning Streak */}
      <Card>
        <Card.Header>
          <Card.Title>Learning Streak</Card.Title>
        </Card.Header>
        <Card.Content>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="text-4xl font-bold text-primary-600">
                {enrolledCourses.length > 0 ? Math.min(7, Math.floor(enrolledCourses.length / 2) + 1) : 0}
              </div>
              <div>
                <p className="text-lg font-semibold text-secondary-900">Day Streak</p>
                <p className="text-sm text-secondary-600">
                  {enrolledCourses.length > 0 ? "Keep it up! You're on fire 🔥" : "Start learning to build your streak!"}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              <span className="text-sm font-medium text-green-600">
                {enrolledCourses.length > 0 ? `+${Math.floor(enrolledCourses.length / 2)} days this week` : "Start your journey"}
              </span>
            </div>
          </div>
        </Card.Content>
      </Card>
    </div>
  );
};

export default StudentDashboard;
